
import { Component, Vue, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import ActivityLogRepository from '@/api/repositories/ActivityLogRepository';

const activityLogRepository: ActivityLogRepository = RepositoryFactory.get('activityLog');

/**
 * Mirrors the backend's descriptor allowlist (misc/activityLog.descriptors.ts).
 * A backend action missing here still shows up in rows; it just cannot be
 * picked in the filter until this list is extended — same trade-off as the
 * hardcoded option lists on the SMS monitor.
 */
const ACTION_OPTIONS = [
    'company.addTaxi', 'company.create', 'company.edit', 'company.recycle',
    'company.remove', 'company.resendInvite',
    'customer.block', 'customer.edit', 'customer.remove', 'customer.unblock',
    'driver.block', 'driver.createDriverForCompany', 'driver.delete',
    'driver.edit', 'driver.recycle', 'driver.unblock',
    'favorite.create', 'favorite.delete', 'favorite.update',
    'manager.createManager', 'manager.delete', 'manager.setStatus',
    'promoter.applyReassignment', 'promoter.create', 'promoter.delete',
    'promoter.markPaid', 'promoter.update',
    'push_notification.sendAudienceNotification', 'push_notification.sendTestNotification',
    'rating.hide', 'rating.moderationDelete', 'rating.restore',
    'ride.delete',
    'share.updatePricePerRide', 'share.updatePricePerRideActive',
    'share.updatePromoterIncentive', 'share.updatePromoterRates', 'share.updateWaitingTime',
    'sms.clearSuperOtp', 'sms.issueCustomerLoginCode', 'sms.issueDriverLoginCode',
    'sms.resend', 'sms.rotateSuperOtp', 'sms.setSuperOtp',
    'taxi.create', 'taxi.edit', 'taxi.remove',
    'transaction.create', 'transaction.setCredit'
];

const ENTITY_TYPE_OPTIONS = [
    'company', 'customer', 'driver', 'favorite', 'manager', 'notification',
    'promoter', 'rating', 'ride', 'setting', 'share', 'sms', 'taxi'
];

@Component({})
export default class ActivityLogView extends Vue {
    public isLoading: boolean = false;

    public items: any[] = [];
    public total: number = 0;
    public options: any = { page: 1, itemsPerPage: 25, sortBy: [], sortDesc: [] };
    public expanded: any[] = [];

    public fromMenu: boolean = false;
    public toMenu: boolean = false;

    public filters: {
        actorId: string | null,
        action: string | null,
        entityType: string | null,
        operation: string | null,
        success: boolean | null,
        from: string | null,
        to: string | null,
        changesOnly: boolean
    } = {
        actorId: null,
        action: null,
        entityType: null,
        operation: null,
        success: null,
        from: null,
        to: null,
        changesOnly: false
    };

    public actorOptions: Array<{ text: string, value: string }> = [];
    public actionOptions = ACTION_OPTIONS;
    public entityTypeOptions = ENTITY_TYPE_OPTIONS;

    /**
     * Identifies the most recent log request; a response is discarded unless
     * its token is still current, so quickly toggled filters cannot resolve
     * out of order.
     */
    private logsToken: number = 0;

    public get operationOptions() {
        return ['CREATE', 'UPDATE', 'DELETE', 'RESTORE', 'ACTION_ONLY'].map((value) => ({
            text: this.$t(`ACTIVITY_LOG.OPERATION.${value}`).toString(),
            value
        }));
    }

    public get resultOptions() {
        return [
            { text: this.$t('ACTIVITY_LOG.RESULT.SUCCESS').toString(), value: true },
            { text: this.$t('ACTIVITY_LOG.RESULT.FAILED').toString(), value: false }
        ];
    }

    public get headers() {
        return [
            { text: this.$t('ACTIVITY_LOG.TABLE.TIME'), value: 'createdAt', sortable: false },
            { text: this.$t('ACTIVITY_LOG.TABLE.ACTOR'), value: 'actor', sortable: false },
            { text: this.$t('ACTIVITY_LOG.TABLE.ACTION'), value: 'action', sortable: false },
            { text: this.$t('ACTIVITY_LOG.TABLE.ENTITY'), value: 'entity', sortable: false },
            { text: this.$t('ACTIVITY_LOG.TABLE.OPERATION'), value: 'operation', sortable: false },
            { text: this.$t('ACTIVITY_LOG.TABLE.RESULT'), value: 'success', sortable: false },
            { text: '', value: 'data-table-expand' }
        ];
    }

    async created() {
        await Promise.all([this.loadLogs(), this.loadActors()]);
    }

    @Watch('options')
    private onOptionsChange() {
        this.loadLogs();
    }

    public onFilterChange() {
        // Reset to first page whenever a filter changes, then reload. Setting
        // options triggers the watcher, which reloads; changing filters on
        // page 1 has no options change, so reload directly.
        if (this.options.page !== 1) {
            this.options = { ...this.options, page: 1 };
        } else {
            this.loadLogs();
        }
    }

    public async loadLogs() {
        const token = ++this.logsToken;
        try {
            this.isLoading = true;
            const response = await activityLogRepository.list({
                page: this.options.page,
                pageSize: this.options.itemsPerPage,
                actorId: this.filters.actorId ?? undefined,
                action: this.filters.action ?? undefined,
                entityType: this.filters.entityType ?? undefined,
                operation: this.filters.operation ?? undefined,
                success: this.filters.success ?? undefined,
                from: this.filters.from ? moment(this.filters.from).startOf('day').toISOString() : undefined,
                to: this.filters.to ? moment(this.filters.to).endOf('day').toISOString() : undefined,
                // The server would let changesOnly override an explicit
                // operation filter; the checkbox is disabled in that case and
                // the value withheld here so the two can never conflict.
                changesOnly: (this.filters.changesOnly && !this.filters.operation) || undefined
            });
            if (token !== this.logsToken) return;
            this.items = response.data.items ?? [];
            this.total = response.data.total ?? 0;
            this.expanded = [];
        } catch (e) {
            if (token !== this.logsToken) return;
            Vue.notifyErrorSimplified('ACTIVITY_LOG.NOTIFICATIONS.LOAD_ERROR');
        } finally {
            if (token === this.logsToken) this.isLoading = false;
        }
    }

    /**
     * The actor filter offers the distinct actors that appear in the log
     * itself (SYSTEM rows carry no actorId and are not covered by it).
     * Non-fatal: if the list cannot be loaded the filter simply stays empty.
     */
    private async loadActors() {
        try {
            const response = await activityLogRepository.listActors();
            this.actorOptions = (response.data ?? [])
                .map((actor: any) => ({
                    text: actor.actorLabel || actor.actorId,
                    value: actor.actorId
                }))
                .filter((option: any) => option.text && option.value);
        } catch (e) {
            // Filter stays empty.
        }
    }

    public operationColor(operation: string): string {
        switch (operation) {
            case 'CREATE': return 'green';
            case 'UPDATE': return 'blue';
            case 'DELETE': return 'red';
            case 'RESTORE': return 'orange';
            default: return 'grey';
        }
    }

    public formatValue(value: any): string {
        if (value === null || value === undefined) {
            return '—';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value, null, 2);
        }
        return String(value);
    }

    public hasParams(item: any): boolean {
        return !!item.params && Object.keys(item.params).length > 0;
    }
}
